import {useState, useEffect} from 'react';
import {useNavigate} from 'react-router-dom';
import {isTokenTrusted, clearSession} from '../utils/tokenGuard';

/**
 * Session guard hook — call once at the top of any protected component.
 *
 * On mount:
 *   - Reads guest_token from localStorage
 *   - Validates format and expiry via isTokenTrusted()
 *   - If invalid: clears session, redirects to /expired immediately
 *
 * While mounted:
 *   - Listens to the `storage` event (fires in OTHER tabs when localStorage changes)
 *   - If guest_token is removed or replaced with an untrusted value → kill session
 *   - This closes the gap where a user signs out in Tab A but Tab B stays open
 *
 * Returns:
 *   { valid: boolean }
 *   Callers should render nothing (return null) while valid === false to prevent
 *   a flash of protected content before the redirect fires.
 */
export function useSessionGuard(): {valid: boolean} {
  const navigate = useNavigate();

  // Evaluate trust synchronously in the initialiser so the very first render
  // already knows whether to show content or not — no flicker.
  const [valid] = useState<boolean>(() =>
    isTokenTrusted(localStorage.getItem('guest_token')),
  );

  useEffect(() => {
    // If the synchronous check already failed, redirect now.
    if (!valid) {
      clearSession();
      navigate('/expired', {replace: true});
      return;
    }

    // Cross-tab synchronisation via the Storage API.
    // The `storage` event fires in every tab EXCEPT the one that made the change.
    const onStorageChange = (event: StorageEvent) => {
      if (event.key !== 'guest_token') return;

      const newToken = event.newValue;

      // Token was deleted or replaced with something untrustworthy
      if (!isTokenTrusted(newToken)) {
        clearSession();
        navigate('/expired', {replace: true});
      }
    };

    window.addEventListener('storage', onStorageChange);

    // Visibility change: when the tab comes back into focus after being hidden,
    // re-validate the token. The user may have logged out in another tab while
    // this one was in the background, or the token may have expired while idle.
    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      if (!isTokenTrusted(localStorage.getItem('guest_token'))) {
        clearSession();
        navigate('/expired', {replace: true});
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      window.removeEventListener('storage', onStorageChange);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [valid, navigate]);

  return {valid};
}
